// @flow

/* ***************************************************************************
* @author mfranco (mfranco@mobinteg.com)
*
*   Contains functions to calculate debtValue, maxValue, numberOfPayments,
* payments by numberOfPayments, payments by value, paymentDates, update Payment
* values when one of them has been changed and update debtValue
*******************************************************************************/

import _ from "lodash"

export const calculateNumberOfPayments = ( debtValue: number, valuePerPayment: number ): number => {
  return Math.ceil( debtValue / valuePerPayment )
}

export const calculatePayments = ( debtValue: number, howManyPayments: number ): [number] => {

  let paymentValue = _.round( ( debtValue / howManyPayments ), 2 )
  let paymentsDifference = _.round( ( paymentValue * howManyPayments ) - debtValue, 2 )
  let firstPaymentValue = _.round( ( paymentValue - paymentsDifference ), 2 )

  return _.times( howManyPayments, i => {
    return i === 0 ? firstPaymentValue : paymentValue
  } )
}

export const calculatePaymentsByValue = ( debtValue: number, howManyPayments: number,
  valuePerPayment: number ): number[] => {

  return _.times( howManyPayments, i => {
    const payment = i + 1 === howManyPayments ? _.round( debtValue, 2 ) : valuePerPayment
    debtValue = debtValue - valuePerPayment
    return payment
  } )
}

const getMaxValue = ( newPayments ) => {
  return _.max( newPayments )
}

export const handleChangedPayments = ( event, isChangedPayment, payments, debtValue ) => {

  let numberOfChangedValues = isChangedPayment
    .filter( payment => payment === true ).length
  let paymentsCopy = [ ...payments ]
  let newPayments = []
  let paymentsTotal

  let totalTrueValue = ( event === undefined ) ? calculateTrueValue( paymentsCopy, isChangedPayment ) : 0
  paymentsTotal = _.sum( paymentsCopy )
  const valueToAdd = debtValue - paymentsTotal
  const numberOfPaymentsToEdit = paymentsCopy.length - numberOfChangedValues
  let dividedValueToAdd = ( ( valueToAdd + totalTrueValue ) / numberOfPaymentsToEdit )
  let firstUpdatedPaymentValue = _.round( ( ( ( valueToAdd + totalTrueValue ) -
  ( _.round( dividedValueToAdd, 2 ) * numberOfPaymentsToEdit ) ) + dividedValueToAdd ), 2 )

  newPayments = updatePayments( paymentsCopy, isChangedPayment,
    true, event, firstUpdatedPaymentValue, dividedValueToAdd )

  let debt = numberOfChangedValues === payments.length ? paymentsTotal : debtValue
  let newIsChanged = numberOfChangedValues === payments.length ? [] : [ ...isChangedPayment ]

  return {
    debtValue: debt,
    payments: newPayments,
    isChangedPayment: newIsChanged,
    valuePerPayment: getMaxValue( newPayments ),
  }
}

const updatePayments = ( oldPayments, isChangedPayment, isUpdated, value, firstUpdatedPaymentValue,
  dividedValueToAdd ) => {

  let newPayments = []
  _.times( oldPayments.length, i => {
    let payment = oldPayments[ i ]
    if ( isChangedPayment[ i ] ) {
      newPayments.push( _.round( payment, 2 ) )
    } else {
      payment = value === undefined ? 0 : payment
      if ( isUpdated ) {
        newPayments.push( _.round( ( payment + firstUpdatedPaymentValue ), 2 ) )
        isUpdated = false
      } else {
        newPayments.push( _.round( ( payment + ( dividedValueToAdd ) ), 2 ) )
      }
    }
  } )
  return newPayments
}

// sums the values that haven't been changed
const calculateTrueValue = ( payments, isChangedPayment ) => {
  let totalTrueValue = 0
  _.times( payments.length, i => {
    if ( payments[ i ] === undefined ) {
      payments[ i ] = 0
    }
    if ( !isChangedPayment[ i ] ) {
      totalTrueValue = totalTrueValue + payments[ i ]
    }
  } )
  return totalTrueValue
}

export const updateDates = ( numberOfPayments: number, firstPaymentDate: Date ): Date[] => {

  let payDates = []

  if ( firstPaymentDate && numberOfPayments ) {
    _.times( numberOfPayments, i => {

      let newDate = new Date( firstPaymentDate )
      newDate.setMonth( newDate.getMonth() + i )
      payDates.push( newDate )
    } )
  }
  return payDates
}

export const handleChangedCapitalValue = ( debt, justiceTax, interestValue, numberOfPayments,
  capitalValue, valuePerPayment, firstPaymentDate, isByPayments ) => {

  let numberOfPays
  let newPayments

  const newCapitalValue = capitalValue ? parseFloat( capitalValue ) : 0
  const newJusticeTax = justiceTax ? parseFloat( justiceTax ) : 0
  const newInterestValue = interestValue ? parseFloat( interestValue ) : 0

  const newDebtValue = newCapitalValue + newJusticeTax + newInterestValue

  if ( isByPayments ) {
    numberOfPays = numberOfPayments
    newPayments = calculatePayments( newDebtValue, numberOfPays )
  } else {
    numberOfPays = calculateNumberOfPayments( newDebtValue, valuePerPayment )
    newPayments = calculatePaymentsByValue( newDebtValue, numberOfPays, valuePerPayment )
  }

  let payDates = updateDates( numberOfPays, firstPaymentDate )

  let maxValue = getMaxValue( newPayments )

  return {
    debtValue: _.round( newDebtValue, 2 ),
    numberOfPayments: isNaN( maxValue ) || numberOfPays === Infinity ? undefined : numberOfPays,
    payments: newPayments,
    valuePerPayment: isNaN( maxValue ) ? undefined : maxValue,
    paymentDates: payDates,
    isChangedPayment: [],
  }
}

export const handleChangedDebt = ( debt, justiceTax, interestValue, numberOfPayments,
  capitalValue, valuePerPayment, firstPaymentDate, isByPayments ) => {

  let newDebtValue
  let numberOfPays
  let newPayments

  let newJusticeTax = justiceTax ? justiceTax : 0
  let newInterestValue = interestValue ? interestValue : 0
  let trueDebtValue = capitalValue + newJusticeTax + newInterestValue

  newDebtValue = debt === undefined && numberOfPayments ? trueDebtValue : null

  newDebtValue = debt !== undefined ? debt : trueDebtValue


  if ( isByPayments ) {
    numberOfPays = numberOfPayments
    newPayments = calculatePayments( newDebtValue, numberOfPays )
  } else {
    numberOfPays = calculateNumberOfPayments( newDebtValue, valuePerPayment )
    newPayments = calculatePaymentsByValue( newDebtValue, numberOfPays, valuePerPayment )
  }

  let payDates = updateDates( numberOfPays, firstPaymentDate )

  let maxValue = getMaxValue( newPayments )

  return {
    numberOfPayments: isNaN( maxValue ) || numberOfPays === Infinity ? undefined : numberOfPays,
    payments: newPayments,
    valuePerPayment: isNaN( maxValue ) ? undefined : maxValue,
    paymentDates: payDates,
    debtValue: _.round( newDebtValue, 2 ),
    isChangedPayment: [],
  }
}

export const handlePayments = ( numberOfPayments, debtValue, firstPaymentDate ) => {

  let maxValue = undefined
  let newPayments = []
  let payDates = {}

  if ( numberOfPayments ) {
    payDates = updateDates( numberOfPayments, firstPaymentDate )

    newPayments = calculatePayments( debtValue, numberOfPayments )

    maxValue = getMaxValue( newPayments )
  }

  return {
    payments: newPayments,
    valuePerPayment: maxValue,
    paymentDates: payDates,
    isChangedPayment: [],
    arePaymentInputsValid: [ false, false ],
    isByPayments: true,
  }
}

export const handlePaymentByValue = ( valuePerPayment, debtValue, firstPaymentDate ) => {

  let howManyPayments = undefined
  let payDates = []
  let newPayments = []

  if ( valuePerPayment ) {

    howManyPayments = calculateNumberOfPayments( debtValue, valuePerPayment )

    payDates = updateDates( howManyPayments, firstPaymentDate )

    newPayments = calculatePaymentsByValue( debtValue, howManyPayments, valuePerPayment )

  }
  return {
    numberOfPayments: howManyPayments,
    payments: newPayments,
    paymentDates: payDates,
    isChangedPayment: [],
    arePaymentInputsValid: [ false, false ],
    isByPayments: false,
  }
}

export const calculateInterestValue = ( value, invoiceExpiryDates, firstPaymentDate,
  checkBoxesInvoices, justiceTax, capitalValue, interestValue, numberOfPayments, valuePerPayment, isByPayments ) => {

  let newInterestValue = 0
  let firstDateInterestValue = 0
  const interestRateTime = 365

  const oneDay = 24 * 60 * 60 * 1000
  const expiryDates = Object.entries( invoiceExpiryDates )
  const firstDate = new Date( firstPaymentDate.toDateString() )

  expiryDates.forEach( date => {
    if ( date[ 1 ] ) {
      const expiryDate = new Date ( date[ 1 ] )
      const daysBetween = ( firstDate.getTime() - expiryDate.getTime() ) / ( oneDay )
      const newInterestRate = ( daysBetween / interestRateTime ) * value

      const interestValue = ( newInterestRate * checkBoxesInvoices[ date[ 0 ] ] ) / 100
      firstDateInterestValue = firstDateInterestValue + interestValue
      newInterestValue = firstDateInterestValue
    }
  } )

  let newJusticeTax = justiceTax ? justiceTax : 0

  let newDebtValue = firstDateInterestValue ?
    _.round( ( capitalValue + newJusticeTax + firstDateInterestValue ), 2 ) :
    capitalValue + newJusticeTax

  const state = handleChangedDebt( newDebtValue,
    justiceTax, interestValue,
    numberOfPayments, capitalValue,
    valuePerPayment, firstPaymentDate,
    isByPayments )

  return {
    ...state,
    interestValue: newInterestValue ? _.round( newInterestValue, 2 ) : undefined,
    interestRate: value,
  }
}



// WEBPACK FOOTER //
// ./src/paymentAgreementCalculation.jsx